import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CssBaseline,
    Container,
    CircularProgress,
    InputAdornment,
    IconButton,
    LinearProgress,
    Paper,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import logo from '../../images/logo.jpeg'; // Updated logo import

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.cardireach.com/api';

interface PasswordStrength {
    score: number;
    label: string;
    color: string;
}

export default function SetupPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [token, setToken] = useState<string>('');
    const [userId, setUserId] = useState<string>('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [validatingToken, setValidatingToken] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [countdown, setCountdown] = useState(3);

    // Password strength calculation
    const calculatePasswordStrength = (pwd: string): PasswordStrength => {
        let score = 0;

        if (pwd.length >= 8) score++;
        if (pwd.length >= 12) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;

        if (score <= 2) {
            return { score, label: 'Weak', color: '#f44336' };
        } else if (score <= 4) {
            return { score, label: 'Medium', color: '#ff9800' };
        } else {
            return { score, label: 'Strong', color: '#4caf50' };
        }
    };

    const passwordStrength = calculatePasswordStrength(password);

    // Validate token on component mount
    useEffect(() => {
        const validateToken = async () => {
            const tokenParam = searchParams.get('token');
            const userIdParam = searchParams.get('user_id');

            if (!tokenParam || !userIdParam) {
                setError('Invalid or missing token. Please use the link from your email.');
                setValidatingToken(false);
                return;
            }

            setToken(tokenParam);
            setUserId(userIdParam);

            try {
                // Validate token with backend
                const response = await axios.post(`${API_BASE_URL}/auth/validate-setup-token`, {
                    token: tokenParam,
                    user_id: userIdParam,
                });

                if (response.data.valid) {
                    setTokenValid(true);
                } else {
                    setError('Invalid or expired token. Please request a new setup link.');
                }
            } catch (err: any) {
                if (err.response?.status === 404 || err.response?.status === 400) {
                    setError('Invalid or expired token. Please request a new setup link.');
                } else {
                    setError('Unable to validate token. Please try again later.');
                }
            } finally {
                setValidatingToken(false);
            }
        };

        validateToken();
    }, [searchParams]);

    // Countdown timer for redirect
    useEffect(() => {
        if (success && countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else if (success && countdown === 0) {
            navigate('/login');
        }
    }, [success, countdown, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validation
        if (!password || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (passwordStrength.score < 3) {
            setError('Password is too weak. Please choose a stronger password.');
            return;
        }

        try {
            setLoading(true);
            await axios.post(`${API_BASE_URL}/auth/setup-password`, {
                token,
                user_id: userId,
                password,
            });

            setSuccess('Password set successfully! Redirecting to login...');
        } catch (err: any) {
            if (err.response?.data?.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Failed to set password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const commonPageContainerSx = {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        backgroundImage: 'url(/assets/setup_password.png)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        padding: { xs: 2, md: 8 },
    };

    const commonCardSx = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '450px',
        borderRadius: 4,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: { xs: 3, sm: 4 },
    };

    const logoSx = {
        width: 120,
        height: 120,
        objectFit: 'contain',
        mb: 2,
        display: 'block',
        mx: 'auto', // Center the logo horizontally
    };


    // Show loading state while validating token
    if (validatingToken) {
        return (
            <Box sx={commonPageContainerSx}>
                <CssBaseline />
                <Paper elevation={6} sx={{ ...commonCardSx, textAlign: 'center', py: 6 }}>
                    <Box
                        component="img"
                        src={logo}
                        alt="Cardireach Logo"
                        sx={logoSx}
                    />
                    <CircularProgress size={60} />
                    <Typography variant="h6" sx={{ mt: 3 }}>
                        Validating your link...
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // Show error if token is invalid
    if (!tokenValid) {
        return (
            <Box sx={commonPageContainerSx}>
                <CssBaseline />
                <Paper elevation={6} sx={commonCardSx}>
                    <Box
                        component="img"
                        src={logo}
                        alt="Cardireach Logo"
                        sx={logoSx}
                    />
                    <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Setup Password
                    </Typography>
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                        {error}
                    </Alert>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{ mt: 3 }}
                    >
                        Go to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={commonPageContainerSx}>
            <CssBaseline />
            <Paper elevation={6} sx={commonCardSx}>
                <Box
                    component="img"
                    src={logo}
                    alt="Cardireach Logo"
                    sx={logoSx}
                />
                <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Create Your Password
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Set up a strong password for your account
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success} ({countdown}s)
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        autoFocus
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading || !!success}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Password Strength Indicator */}
                    {password && (
                        <Box sx={{ mt: 1, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Password Strength
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600, color: passwordStrength.color }}
                                >
                                    {passwordStrength.label}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={(passwordStrength.score / 6) * 100}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(0,0,0,0.1)',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: passwordStrength.color,
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={loading || !!success}
                        error={confirmPassword !== '' && password !== confirmPassword}
                        helperText={
                            confirmPassword !== '' && password !== confirmPassword
                                ? 'Passwords do not match'
                                : ''
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Password Requirements */}
                    {password && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                bgcolor: 'rgba(0,0,0,0.02)',
                                borderRadius: 2,
                                border: '1px solid rgba(0,0,0,0.1)',
                            }}
                        >
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Password Requirements:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle
                                        fontSize="small"
                                        sx={{
                                            color: password.length >= 8 ? '#4caf50' : 'rgba(0,0,0,0.26)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: password.length >= 8 ? '#4caf50' : 'text.secondary',
                                        }}
                                    >
                                        At least 8 characters
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle
                                        fontSize="small"
                                        sx={{
                                            color: /[A-Z]/.test(password) ? '#4caf50' : 'rgba(0,0,0,0.26)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: /[A-Z]/.test(password) ? '#4caf50' : 'text.secondary',
                                        }}
                                    >
                                        One uppercase letter
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle
                                        fontSize="small"
                                        sx={{
                                            color: /[a-z]/.test(password) ? '#4caf50' : 'rgba(0,0,0,0.26)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: /[a-z]/.test(password) ? '#4caf50' : 'text.secondary',
                                        }}
                                    >
                                        One lowercase letter
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle
                                        fontSize="small"
                                        sx={{
                                            color: /[0-9]/.test(password) ? '#4caf50' : 'rgba(0,0,0,0.26)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: /[0-9]/.test(password) ? '#4caf50' : 'text.secondary',
                                        }}
                                    >
                                        One number
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle
                                        fontSize="small"
                                        sx={{
                                            color: /[^A-Za-z0-9]/.test(password)
                                                ? '#4caf50'
                                                : 'rgba(0,0,0,0.26)',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: /[^A-Za-z0-9]/.test(password)
                                                ? '#4caf50'
                                                : 'text.secondary',
                                        }}
                                    >
                                        One special character
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{ mt: 3, mb: 2, py: 1.5 }}
                        disabled={loading || !!success}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                Setting Password...
                            </>
                        ) : (
                            'Set Password'
                        )}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/login')}
                        disabled={loading || !!success}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
}
